// convex/lib/config/domains/credit_notes.ts

import { validatorInvoice } from "../../../companies/invoices";
import { validatorClient } from "../../../companies/clients";
import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";
import { validatorCreditNote } from "../../../companies/credit_notes";
import { creditNotesConfigurationValidator } from "../../../companies/credit_notes/configuration";

// ============================================================================
// CREDIT NOTES DOMAIN - Status Values for i18n
// ============================================================================

export const CREDIT_NOTE_STATUSES = ["draft", "issued", "voided"] as const;

export const CREDIT_NOTE_REASONS = [
  "return",
  "discount",
  "correction",
  "cancellation",
  "other",
] as const;

export const creditNotesDomain: DomainGroup = {
  id: "credit_notes",
  name: "Notas de Crédito",
  description: "Gestión de notas de crédito fiscales",
  icon: "FileDown",
  order: 20,
  status: "active",
  // Solo navegación: se muestra bajo el grupo "Facturación" (junto a Pagos,
  // Créditos, Exenciones). Permisos y tablas siguen en este dominio.
  navParentGroup: "invoices",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "credit_notes",
    keys: {
      title: "credit_notes.title",
      description: "credit_notes.description",
    },
  },

  modules: [
    {
      id: "credit_notes",
      name: "Notas de Crédito",
      description: "Emisión y gestión de notas de crédito",
      icon: "FileDown",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/invoices/credit-notes"],
      navigation: {
        showInNav: true,
        contexts: ["main", "billing"],
        navOrder: { main: 6, billing: 7 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "credit_notes",
        keys: {
          title: "credit_notes.title",
          description: "credit_notes.description",
          longDescription: "credit_notes.longDescription",
          searchPlaceholder: "credit_notes.searchPlaceholder",
          emptyState: "credit_notes.emptyState",
          actions: {
            create: "credit_notes.actions.create",
            edit: "credit_notes.actions.edit",
            delete: "credit_notes.actions.delete",
            view: "credit_notes.actions.view",
          },
        },
        translatableFields: ["notes"],
        translatableEnums: [
          { field: "status", values: CREDIT_NOTE_STATUSES },
          { field: "reason", values: CREDIT_NOTE_REASONS },
        ],
      },

      tables: [
        {
          tableName: "credit_notes",
          schema: validatorCreditNote,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["name", "contactPerson"],
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["correlative", "notes"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "invoices" }, { tableName: "clients" }],
    },
    {
      id: "credit_notes_configurations",
      name: "Notas de Crédito",
      description: "Configuracion de correlativos para notas de credito",
      icon: "Settings",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/invoices/credit-notes"],
      navigation: {
        showInNav: false,
        contexts: ["settings"],
        navOrder: 4,
        status: "active",
      },
      i18n: {
        namespace: "credit_notes.credit_notes_configurations",
        keys: {
          title: "credit_notes.credit_notes_configurations.title",
          description: "credit_notes.credit_notes_configurations.description",
          longDescription:
            "credit_notes.credit_notes_configurations.longDescription",
          searchPlaceholder:
            "credit_notes.credit_notes_configurations.searchPlaceholder",
          emptyState: "credit_notes.credit_notes_configurations.emptyState",
          actions: {
            create: "credit_notes.credit_notes_configurations.actions.create",
            edit: "credit_notes.credit_notes_configurations.actions.edit",
            delete: "credit_notes.credit_notes_configurations.actions.delete",
            view: "credit_notes.credit_notes_configurations.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "credit_notes_configurations",
          schema: creditNotesConfigurationValidator,
          scope: "branch" as TableScope,
          relations: [],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "correlatives" }],
    },
  ],
};
